// Casa address-result codes returned by the worker's /_address-input/place
// handler for casa-source predictions. Mirrored from
// cinderblocks/src/helpers/addressValidation.ts so beacon can do the same
// pre-submit gating as cosmos's NewSellerFlowAddressInput without depending on
// cinderblocks (which transitively pulls in bricks-next).
export type CasaResult =
  | 'ADDRESS_COMING_SOON'
  | 'ADDRESS_EXTRANEOUS_INPUT'
  | 'ADDRESS_INVALID'
  | 'ADDRESS_INVALID_BOX_NUMBER'
  | 'ADDRESS_INVALID_PREMISE'
  | 'ADDRESS_INVALID_STREET'
  | 'ADDRESS_INVALID_SUB_PREMISE'
  | 'ADDRESS_MISSING_BOX_NUMBER'
  | 'ADDRESS_MISSING_PREMISE'
  | 'ADDRESS_MISSING_SUB_PREMISE'
  | 'ADDRESS_NIL'
  | 'ADDRESS_NOT_REAL'
  | 'ADDRESS_NOT_SUPPORTED'
  | 'ADDRESS_UNKNOWN'
  | 'ADDRESS_VACANT'
  | 'ADDRESS_VALID'
  | 'ADDRESS_VALID_STREET';

// Codes that should drop the user into the manual-entry form instead of the
// confirmation modal. Source: cinderblocks ADDRESS_INVALID_RESULT, kept in
// sync intentionally; both surfaces feed the same downstream Athena mutation.
export const ADDRESS_INVALID_RESULT: readonly CasaResult[] = [
  'ADDRESS_NIL',
  'ADDRESS_UNKNOWN',
  'ADDRESS_MISSING_PREMISE',
  'ADDRESS_INVALID_PREMISE',
  'ADDRESS_NOT_SUPPORTED',
  'ADDRESS_NOT_REAL',
  'ADDRESS_INVALID',
  'ADDRESS_INVALID_STREET',
  'ADDRESS_INVALID_SUB_PREMISE',
  'ADDRESS_MISSING_BOX_NUMBER',
  'ADDRESS_INVALID_BOX_NUMBER',
  'ADDRESS_EXTRANEOUS_INPUT',
];

export interface AddressValidationGating {
  needsManualEntry: boolean;
  needsUnitSelection: boolean;
}

// Pure read of the casa result-code array. The worker only attaches `result`
// for casa-source predictions (google fallbacks have no codes), so an absent
// or empty array intentionally returns false/false — the existing happy path.
export function gateAddressValidation(
  result: readonly CasaResult[] | readonly string[] | undefined,
): AddressValidationGating {
  if (!result || result.length === 0) {
    return { needsManualEntry: false, needsUnitSelection: false };
  }
  const codes = result as readonly string[];
  return {
    needsManualEntry: ADDRESS_INVALID_RESULT.some((code) => codes.includes(code)),
    needsUnitSelection: codes.includes('ADDRESS_MISSING_SUB_PREMISE'),
  };
}

export interface AddressComponents {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  unit?: string;
  id?: string;
  latitude?: number;
  longitude?: number;
  result?: CasaResult[];
  source?: 'google' | 'casa';
}

export interface AddressSuggestion {
  description: string;
  placeId?: string;
  addressToken?: string;
  source: 'google' | 'casa';
  /** Structured components, present when Casa autocomplete resolved the address. */
  components?: AddressComponents;
}

interface GooglePrediction {
  description: string;
  place_id?: string;
}

interface CasaPrediction {
  description?: string;
  formattedAddress?: string;
  addressToken: string;
  street?: string;
  unitInfo?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

type RawPrediction = GooglePrediction | CasaPrediction;

interface AutocompleteResponse {
  predictions?: RawPrediction[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isCasaPrediction(p: unknown): p is CasaPrediction {
  return isRecord(p) && typeof p.addressToken === 'string';
}

function isGooglePrediction(value: unknown): value is GooglePrediction {
  return isRecord(value) && typeof value.description === 'string';
}

function isRawPrediction(value: unknown): value is RawPrediction {
  return isCasaPrediction(value) || isGooglePrediction(value);
}

function normalize(p: RawPrediction): AddressSuggestion {
  if (isCasaPrediction(p)) {
    const components =
      p.street && p.city && p.state && p.postalCode
        ? {
            street: p.street,
            city: p.city,
            state: p.state,
            postalCode: p.postalCode,
            id: p.addressToken,
            source: 'casa' as const,
            ...(p.unitInfo ? { unit: p.unitInfo } : {}),
          }
        : undefined;
    return {
      description: p.description ?? p.formattedAddress ?? '',
      addressToken: p.addressToken,
      source: 'casa',
      components,
    };
  }
  return {
    description: p.description ?? '',
    placeId: p.place_id,
    source: 'google',
  };
}

interface PlaceResponse {
  id?: string;
  street1?: string;
  unit?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  result?: CasaResult[];
  source?: 'google' | 'casa';
}

function parsePlaceResponse(value: unknown): PlaceResponse | null {
  if (!isRecord(value)) return null;
  return {
    ...(typeof value.id === 'string' ? { id: value.id } : {}),
    ...(typeof value.street1 === 'string' ? { street1: value.street1 } : {}),
    ...(typeof value.unit === 'string' ? { unit: value.unit } : {}),
    ...(typeof value.city === 'string' ? { city: value.city } : {}),
    ...(typeof value.state === 'string' ? { state: value.state } : {}),
    ...(typeof value.postal_code === 'string' ? { postal_code: value.postal_code } : {}),
    ...(typeof value.latitude === 'number' ? { latitude: value.latitude } : {}),
    ...(typeof value.longitude === 'number' ? { longitude: value.longitude } : {}),
    ...(Array.isArray(value.result) && value.result.every((item) => typeof item === 'string')
      ? { result: value.result as CasaResult[] }
      : {}),
    ...(value.source === 'google' || value.source === 'casa' ? { source: value.source } : {}),
  };
}

function parseAutocompleteResponse(value: unknown): AutocompleteResponse {
  if (!isRecord(value) || !Array.isArray(value.predictions)) return {};
  return { predictions: value.predictions.filter(isRawPrediction) };
}

// Resolves a suggestion to structured components via the /place endpoint when
// not already inline, mirroring the production homepage. Returns null if unresolved.
export async function resolveAddressComponents(
  suggestion: AddressSuggestion,
  sessionToken: string,
): Promise<AddressComponents | null> {
  if (suggestion.components && !suggestion.addressToken) return suggestion.components;

  const params = new URLSearchParams();
  if (suggestion.addressToken) {
    params.set('addressToken', suggestion.addressToken);
  } else if (suggestion.placeId) {
    params.set('placeId', suggestion.placeId);
    params.set('sessionToken', sessionToken);
  } else {
    return null;
  }

  let data: PlaceResponse;
  try {
    const res = await fetch(`/_address-input/place?${params.toString()}`);
    if (!res.ok) return suggestion.components ?? null;
    data = parsePlaceResponse(parseJson(await res.text())) ?? {};
  } catch {
    return suggestion.components ?? null;
  }

  if (!data.street1 || !data.city || !data.state || !data.postal_code) {
    return suggestion.components ?? null;
  }
  return {
    street: data.street1,
    city: data.city,
    state: data.state,
    postalCode: data.postal_code,
    ...(data.unit ? { unit: data.unit } : {}),
    ...(data.id ? { id: data.id } : {}),
    ...(data.latitude !== undefined ? { latitude: data.latitude } : {}),
    ...(data.longitude !== undefined ? { longitude: data.longitude } : {}),
    ...(data.result ? { result: data.result } : {}),
    ...(data.source ? { source: data.source } : {}),
  };
}

// Cosmos parity (cinderblocks addressValidation.ts): when the autocomplete
// resolves a google-source prediction, run it through Casa's findByFields to
// either upgrade to a Casa-canonical address (with real result codes) or
// learn it isn't standardizable. The worker handler returns the original
// google fields plus `result: ['ADDRESS_UNKNOWN']` on Casa NIL / Athena
// failure so the existing gateAddressValidation flow naturally trips the
// invalid-address gate and redirects to /manual-address-entry; on success
// the returned components are the Casa-standardized version and downstream
// gating (unit selection, manual-entry) uses those.
//
// Returns null only when the round-trip itself fails (network reject, etc.);
// the caller treats that the same way as needsManualEntry to stay
// conservative when Casa is unreachable.
export async function standardizeAddress(
  components: AddressComponents,
): Promise<AddressComponents | null> {
  try {
    const res = await fetch('/_address-input/standardize', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        street1: components.street,
        city: components.city,
        state: components.state,
        postal_code: components.postalCode,
        ...(components.unit ? { unit: components.unit } : {}),
      }),
    });
    if (!res.ok) return null;
    const data = parsePlaceResponse(parseJson(await res.text()));
    if (!data || !data.street1 || !data.city || !data.state || !data.postal_code) return null;
    return {
      street: data.street1,
      city: data.city,
      state: data.state,
      postalCode: data.postal_code,
      ...(data.unit ? { unit: data.unit } : {}),
      ...(data.id ? { id: data.id } : {}),
      ...(data.latitude !== undefined ? { latitude: data.latitude } : {}),
      ...(data.longitude !== undefined ? { longitude: data.longitude } : {}),
      ...(data.result ? { result: data.result } : {}),
      ...(data.source ? { source: data.source } : {}),
    };
  } catch {
    return null;
  }
}

// Casa's autocomplete/place response only flags ADDRESS_MISSING_SUB_PREMISE
// when Casa is confident the address has multiple units. Buildings whose
// canonical Casa entry doesn't carry that flag still come back without it,
// even though the user really does need to pick a unit. This calls the
// worker's /_address-input/list-units endpoint (which wraps Athena's
// casa.listUnits query) to make the pre-submit unit-prompt decision match
// cosmos's behavior (cinderblocks doesAddressHaveMultipleUnits).
//
// Best-effort: any failure (network, non-2xx, malformed JSON) resolves to
// false so the existing confirmation modal still opens. Athena's address
// resolver re-runs the same check server-side after lead creation, so worst
// case we lose the pre-submit prompt nicety — the downstream flow still
// surfaces the unit picker.
export async function fetchHasMultipleUnits(addressUuid: string): Promise<boolean> {
  if (!addressUuid) return false;
  try {
    const res = await fetch(
      `/_address-input/list-units?addressUuid=${encodeURIComponent(addressUuid)}`,
    );
    if (!res.ok) return false;
    const parsed = parseJson(await res.text());
    return isRecord(parsed) && parsed.hasMultipleUnits === true;
  } catch {
    return false;
  }
}

export async function fetchAddressSuggestions(
  query: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
  if (!query.trim()) return [];
  const url = `/_address-input/autocomplete?sessionToken=${encodeURIComponent(
    sessionToken,
  )}&query=${encodeURIComponent(query)}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Address autocomplete failed (${res.status})`);
  }
  const data = parseAutocompleteResponse(parseJson(await res.text()));
  return (data.predictions ?? []).map(normalize);
}
